/**
 * What a product is.
 *
 * Shaped from the 1C SKU feed (`/mega/hs/get_sku1/get_sku1`), which sends three
 * option arrays with three different jobs and one naming convention between
 * them. The names here say what each one does rather than what the feed calls
 * it, because `option2` is not a thing anybody can hold in their head.
 */
import type { Money } from './money'

export type Sku = string
export type CategoryId = string

/**
 * Which language the customer is reading in.
 *
 * Named here rather than imported from `@mgc/i18n` because this package imports
 * nothing — it takes its world as parameters, and the locale is part of that
 * world for exactly one reason: which of a product's names is *the* name.
 */
export type Locale = 'mn' | 'en'

/** Which way the customer is taking it. Set per order, overridable per line. */
export type OrderType = 'eat_in' | 'take_out'

/** Hot or iced. Iced only exists when 1C sent an `iceSku`. */
export type Temperature = 'hot' | 'ice'

/** What a menu tile can say, including a drink that is both. */
export type TileTemperature = Temperature | 'both'

/**
 * An add-on: an extra shot, a syrup, oat milk.
 *
 * Priced on top of the line and **never discounted** — a product-level discount
 * applies to the drink, not to the vanilla in it. `code` is the SKU 1C is told
 * about in the line's `adds[]`.
 *
 * From the feed's `options` (which carry `measureunit` and `pce`) and `option2`
 * (which do not). They are the same thing to a customer and are modelled as
 * one; `group` says which array it came from so a checkout payload can be
 * rebuilt exactly.
 */
export interface AddOn {
  readonly code: Sku
  readonly label: string
  readonly price: Money
  /** `measureunit` from the feed — "shot", "ml". Display only. */
  readonly unit?: string
  /** `pce` from the feed — how many of `unit` this add-on is. */
  readonly pieces?: number
  /** Which feed array it came from. Round-trips into the 1C payload. */
  readonly group: 'shots' | 'extras'
}

/**
 * A swap that replaces the line's SKU.
 *
 * This is the one that made the old code hard: choosing it means 1C is told a
 * *different product*, at a different price, and the old cart stored that
 * changed SKU in `productId` — so a later product lookup returned the changed
 * SKU's own full price and `checkout-api.ts` had to subtract the change price
 * and every add-on back out of `item.price` to recover a base. Here the
 * selection keeps the base and the change separately and nothing needs
 * recovering.
 */
export interface ChangeOption {
  /** `codechange` from the feed. Becomes the line's effective SKU. */
  readonly code: Sku
  readonly label: string
  /** Added to the line's own price, not to `adds[]`. */
  readonly price: Money
}

export interface Category {
  readonly id: CategoryId
  readonly name: string
  readonly nameEn?: string
  /**
   * A column, not a hardcoded map.
   *
   * `sku-sync.ts:getCategorySortOrder` was nine names in a `Record` with
   * partial-match fallbacks underneath — data in a source file, so reordering
   * the menu was a release.
   */
  readonly sortOrder: number
  readonly imageUrl?: string
}

export interface Product {
  readonly id: Sku
  readonly sku: Sku
  readonly name: string
  readonly nameEn?: string
  readonly categoryId: CategoryId
  readonly imageUrl?: string

  /** The menu price, VAT inclusive. */
  readonly price: Money
  /**
   * A promotional price, when there is one. `null` — not zero — when there is
   * not: the feed sends `0` for "no discount" and a zero-price product is a
   * thing that could legitimately exist.
   */
  readonly discountedPrice: Money | null

  /** Stock on hand. Ignored when `isInfinite`. */
  readonly balance: number
  readonly isInfinite: boolean

  /** `onlytakein` — cannot be ordered to take away. */
  readonly onlyTakeIn: boolean

  /** `ishot` — served hot by default. With `iceSku`, the customer chooses. */
  readonly isHot: boolean
  /** The SKU 1C wants when the customer chooses iced. */
  readonly iceSku?: Sku
  readonly iceName?: string
  /**
   * The iced cup's own photograph, when somebody has taken one.
   *
   * Not a second column on this product: `iceSku` names a real 1C SKU, that SKU
   * carries its own `menu_item` row like every other, and this is that row's
   * picture carried onto the parent so the page never looks a second product
   * up. Absent for every drink nobody has photographed twice, which is most of
   * them — `imageFor` falls back to `imageUrl`, so a menu with no iced pictures
   * reads exactly as it did before this field existed.
   */
  readonly iceImageUrl?: string

  readonly addOns: readonly AddOn[]
  readonly changeOptions: readonly ChangeOption[]

  readonly enabled: boolean
}

export interface Menu {
  readonly categories: readonly Category[]
  readonly products: readonly Product[]
  /** When the server produced this. The kiosk shows a staleness strip past a threshold. */
  readonly servedAt: string
  /** The branch is shut — 1C's `isclosed`. The kiosk refuses to sell. */
  readonly branchClosed: boolean
}

/**
 * Rails where hot/ice is not a serving fact.
 *
 * 1C's `ice_sku` is the *other* drink — a latte's iced SKU — not a flag that
 * this row is iced. Mega Americano and a frappe have none, same as a cake.
 * `ishot: false` is also the default for all three. Food rails are how a cake
 * is told from a frappe; `other` is the unmatched-feed bucket and is treated
 * as food so a remapped tart does not grow a badge.
 */
const NO_TEMPERATURE_SECTIONS = new Set(['cake', 'salad-sandwich', 'md-products', 'other'])

/** The columns temperature reads. The rest of a product is not this question. */
export type TemperatureFacts = Pick<Product, 'isHot' | 'categoryId'> & Partial<Pick<Product, 'iceSku'>>

function hasIceSku(product: TemperatureFacts): boolean {
  return typeof product.iceSku === 'string' && product.iceSku.length > 0
}

/** Whether the customer must be asked hot or iced before this can be ordered. */
export function needsTemperatureChoice(product: TemperatureFacts): boolean {
  return product.isHot && hasIceSku(product)
}

/**
 * Iced as a serving fact: 1C sent an ice SKU, or this is a drink that is not
 * hot and is not sitting on a food rail.
 */
export function isIceOnly(product: TemperatureFacts): boolean {
  if (product.isHot) return false
  if (hasIceSku(product)) return true
  return !NO_TEMPERATURE_SECTIONS.has(product.categoryId)
}

/**
 * The temperature a line starts with, or `undefined` when there is none to
 * assume — food, and a drink that still has to be chosen.
 */
export function defaultTemperature(product: Product): Temperature | undefined {
  if (needsTemperatureChoice(product)) return undefined
  if (isIceOnly(product)) return 'ice'
  if (product.isHot) return 'hot'
  return undefined
}

/** The columns a picture reads. The rest of a product is not this question. */
export type ImageFacts = Pick<Product, 'imageUrl' | 'iceImageUrl'>

/**
 * The picture for a temperature.
 *
 * Truthy and not `!== undefined`, which is the rule `lineLabel` already applies
 * to `iceName` and for the same reason: an unset column reaches the page as
 * `''` often enough that treating it as a value shows a broken image where the
 * hot cup belongs. The fallback is the product's own picture, so a drink with
 * one photograph behaves as it always has.
 */
export function imageFor(product: ImageFacts, temperature: Temperature | undefined): string | undefined {
  if (temperature === 'ice' && product.iceImageUrl) return product.iceImageUrl
  return product.imageUrl
}

/**
 * What the menu tile states.
 *
 * A hot drink with an `iceSku` is both — the sheet still asks which. A
 * single serving fact (hot-only, ice-only) is that fact. Food is nothing.
 */
export function tileTemperature(product: Product): TileTemperature | undefined {
  if (needsTemperatureChoice(product)) return 'both'
  return defaultTemperature(product)
}

/**
 * Whether the sheet has a choice to make before a line can be written.
 *
 * Temperature, a size, or an extra. A product with none of those can go
 * straight into the basket from the tile.
 */
export function needsConfiguration(product: Product): boolean {
  return needsTemperatureChoice(product) || product.changeOptions.length > 0 || product.addOns.length > 0
}

export function isOrderable(product: Product, orderType: OrderType | null): boolean {
  if (!product.enabled) return false
  if (product.onlyTakeIn && orderType === 'take_out') return false
  return product.isInfinite || product.balance > 0
}

/**
 * How a line is actually served.
 *
 * A line may override the order — a coffee to drink, a cake to take. An
 * `onlyTakeIn` product cannot: 1C will refuse the checkout, so take-away
 * on that line is not a choice the page is allowed to keep.
 */
export function lineOrderType(
  product: Pick<Product, 'onlyTakeIn'>,
  lineType: OrderType | undefined,
  cartType: OrderType | null
): OrderType | null {
  if (product.onlyTakeIn) return 'eat_in'
  return lineType ?? cartType
}

/** The effective price of the drink itself, before change options and add-ons. */
export function basePrice(product: Product): Money {
  const { price, discountedPrice } = product
  if (discountedPrice === null) return price
  // A "discount" above the list price is data the feed should not send; taking
  // the lower of the two is the safe direction and the settings screen reports it.
  return discountedPrice < price ? discountedPrice : price
}

/** True when a promotion is actually reducing this product's price today. */
export function isDiscounted(product: Product): boolean {
  return product.discountedPrice !== null && product.discountedPrice < product.price
}

export function findAddOn(product: Product, code: Sku): AddOn | undefined {
  return product.addOns.find((a) => a.code === code)
}

export function findChangeOption(product: Product, code: Sku): ChangeOption | undefined {
  return product.changeOptions.find((c) => c.code === code)
}

export function categorySort(a: Category, b: Category): number {
  return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'mn')
}

/**
 * The name to put in front of the customer.
 *
 * A product carries two names and only one of them is what the person at the
 * glass can read. `nameEn` was searched from the day search existed and never
 * *displayed* outside the product sheet, so a customer who switched the kiosk
 * to English still met a grid of Mongolian tiles.
 *
 * The fallback is the Mongolian name and not an empty tile: the feed fills
 * `nameEn` for some products and not others, and half a menu in English beats a
 * menu with holes in it.
 *
 * Not for a receipt and not for an order line. Those travel to 1C and onto a
 * fiscal document, where the name has to be the one the feed sent.
 */
export function displayName(item: { readonly name: string; readonly nameEn?: string }, locale: Locale): string {
  return locale === 'en' && item.nameEn !== undefined && item.nameEn.length > 0 ? item.nameEn : item.name
}
